import React, { Component, Fragment } from 'react';
import propTypes from 'prop-types';
import debounce from 'lodash/debounce';

import { PATHNAME_CANVAS } from 'app/Router/const';
import { noop } from 'lib/noop';
import { DragResizer, ToggleFullscreen } from 'ui/Components';

import CanvasControls from './CanvasControls';
import CanvasResizer from './CanvasResizer';
import ExperimentSelection from './ExperimentSelection';
import RenderPreviewSelection from './RenderPreviewSelection';
import styles from './PreviewPane.css';
import { EMPTY_COMPONENT_PAYLOAD, ID, IFRAME_MESSAGE } from '../const';
import * as devices from '../web-interface-devices';

/**
 * preview pane view (renders preview iframes)
 * sends messages to preview iframe based on current preview content and metadata
 */
export default class PreviewPane extends Component {
  static propTypes = {
    allExperimentVariants: propTypes.object,
    componentSelected: propTypes.object.isRequired,
    contentContextStates: propTypes.object,
    contentLinkDisabled: propTypes.bool,
    device: propTypes.string,
    deviceSelected: propTypes.func,
    handleComponentDeselection: propTypes.func.isRequired,
    handleComponentHoverEnter: propTypes.func.isRequired,
    handleComponentHoverLeave: propTypes.func.isRequired,
    handleComponentSelection: propTypes.func.isRequired,
    handleIsMobileSelection: propTypes.func,
    hiddenExperiments: propTypes.object,
    previewContent: propTypes.object,
    previewMetadata: propTypes.object,
    screen: propTypes.object,
    setContentLinkStatus: propTypes.func.isRequired,
    setMWPIframeRef: propTypes.func.isRequired,
    updateContentContextStates: propTypes.func,
    updateHiddenExperiments: propTypes.func
  };

  static defaultProps = {
    allExperimentVariants: {},
    contentContextStates: {},
    contentLinkDisabled: true,
    device: '',
    deviceSelected: noop,
    handleIsMobileSelection: noop,
    hiddenExperiments: {},
    previewContent: {},
    previewMetadata: {},
    screen: window.screen,
    updateContentContextStates: noop,
    updateHiddenExperiments: noop
  };

  constructor(props) {
    super(props);

    this.state = {
      height: devices.RESPONSIVE_940_SIZE.height,
      showFullscreenFrame: false,
      userSetDimensions: false,
      width: devices.RESPONSIVE_940_SIZE.width
    };

    this.frameRuler = React.createRef();
    this.ifr = React.createRef();
    this.ifrMWP = React.createRef();

    this.handleMWPMessage = this.handleMWPMessage.bind(this);
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    this.onDeviceChange = this.onDeviceChange.bind(this);
    this.onFullscreenEnterToggleClick = this.onFullscreenEnterToggleClick.bind(this);
    this.onFullscreenExitToggleClick = this.onFullscreenExitToggleClick.bind(this);
    this.onHeightChange = this.onHeightChange.bind(this);
    this.onWidthChange = this.onWidthChange.bind(this);
    this.refitFrame = this.refitFrame.bind(this);
    this.swipeDiagonally = this.swipeDiagonally.bind(this);
    this.swipeEnd = this.swipeEnd.bind(this);
    this.swipeHorizontally = this.swipeHorizontally.bind(this);
    this.swipeStart = this.swipeStart.bind(this);
    this.swipeVertically = this.swipeVertically.bind(this);
    this.toggleContentLinks = this.toggleContentLinks.bind(this);
    this.updatePreview = debounce(this.updatePreview.bind(this), 100);
  }

  componentDidMount() {
    // listen for messages from MWP iframe
    window.addEventListener('message', this.handleMWPMessage);

    // store the MWP iframe reference in state
    this.props.setMWPIframeRef(this.ifrMWP);

    setTimeout(this.refitFrame, 500);
  }

  componentDidUpdate() {
    this.updatePreview();
  }

  componentWillUnmount() {
    window.removeEventListener('message', this.handleMWPMessage);
    document.removeEventListener('click', this.handleOutsideClick);
  }

  /**
   * handle incoming messages from MWP iframe
   * @param {object} params - method params
   * @param {object} params.data - incoming message data
   */
  handleMWPMessage({ data }) {
    if (!data || typeof data !== 'object' || data.type !== 'MWP') {
      return;
    }

    // unpack data and accept messages from MWP for canvas preview actions
    const { msg: { payload, type } = {} } = data;

    switch (type) {
      case IFRAME_MESSAGE.SELECTING: {
        this.props.handleComponentSelection(payload);
        break;
      }
      case IFRAME_MESSAGE.DESELECTING: {
        this.props.handleComponentDeselection();
        break;
      }
      case IFRAME_MESSAGE.HOVER_ENTER: {
        this.props.handleComponentHoverEnter(payload);
        break;
      }
      case IFRAME_MESSAGE.HOVER_LEAVE: {
        this.props.handleComponentHoverLeave();
        break;
      }
      default:
        break;
    }
  }

  /**
   * Sets selected component same
   * @param {string} deviceName - name of the selected device
   */
  onDeviceChange(deviceName) {
    const device = devices.DESKTOP_DEVICES[deviceName] || devices.MOBILE_DEVICES[deviceName];

    const shouldSetWidthOnly = devices.RESPONSIVE_DESKTOP_DEVICES[deviceName];
    this.onWidthChange(device.width);

    if (shouldSetWidthOnly) {
      this.setState({
        width: device.width,
        userSetDimensions: true
      });
    } else {
      this.setState({
        height: device.height,
        width: device.width,
        userSetDimensions: true
      });
    }

    this.props.deviceSelected(deviceName);
  }

  /**
   * Sets selected width
   * @param {string} width - width selected
   */
  onWidthChange(width) {
    const { handleIsMobileSelection } = this.props;
    const isMobile = width < 940;
    handleIsMobileSelection(isMobile);
    this.setState({
      width,
      userSetDimensions: true
    });
  }

  /**
   * Sets selected height
   * @param {string} height - height selected
   */
  onHeightChange(height) {
    this.setState({
      height,
      userSetDimensions: true
    });
  }

  /**
   * handle toggle enter click
   */
  onFullscreenEnterToggleClick() {
    this.setState(
      ps => ({
        showFullscreenFrame: true,
        userSetHeight: ps.userSetDimensions && ps.height,
        userSetWidth: ps.userSetDimensions && ps.width
      }),
      this.refitFrame
    );
  }

  /**
   * handle toggle exit click
   */
  onFullscreenExitToggleClick() {
    this.setState({ showFullscreenFrame: false }, this.refitFrame);
  }

  get disableContentLinks() {
    return this.props.contentLinkDisabled ? 'none' : 'auto';
  }

  /**
   * compute a new height based on given delta
   *
   * @param {number} delta - change amount
   * @returns {number} new height
   */
  computeNewHeight = delta => {
    const computed = this.state.height + delta;
    const possible = this.props.screen.availHeight - 200;
    return Math.max(0, Math.min(computed, possible));
  };

  /**
   * compute a new width based on given delta
   *
   * @param {number} delta - change amount
   * @returns {number} new width
   */
  computeNewWidth = delta => {
    const computed = this.state.width + delta * 2;
    const possible = this.props.screen.availWidth;
    return Math.max(0, Math.min(computed, possible));
  };

  /**
   * de-select selected component on click outside of iframe and components list
   *
   * @param {object} event - change amount
   */
  handleOutsideClick = event => {
    const excludedComponents = [ID.FRAME_CONTAINER, ID.COMPONENT_LIST, ID.CONTEXT_MENU];

    // The below check makes sure that we handle Component Deselection only if a component is selected
    if (
      this.props.componentSelected.Id !== EMPTY_COMPONENT_PAYLOAD.Id &&
      !excludedComponents.includes(event.target.id)
    ) {
      this.props.handleComponentDeselection();
      this.ifrMWP?.current?.contentWindow.postMessage(
        {
          msg: {
            payload: EMPTY_COMPONENT_PAYLOAD,
            type: IFRAME_MESSAGE.DESELECTING
          },
          type: IFRAME_MESSAGE.CANVAS
        },
        '*'
      );
    }
  };

  /**
   * refit frame based on user settings or autofix/full screen mode
   */
  refitFrame() {
    this.setState(ps => ({
      height: ps.userSetDimensions ? ps.userSetHeight : this.frameRuler.current?.clientHeight,
      width: ps.userSetDimensions ? ps.userSetWidth : devices.RESPONSIVE_940_SIZE.width
    }));
  }

  /**
   * handle horizontal swipes
   * @param {object} e - swipe event parameter.
   */
  swipeHorizontally(e) {
    this.swipeStart(e);
    this.setState({
      userSetDimensions: true,
      width: this.computeNewWidth(e.deltaX)
    });
  }

  /**
   * handle vertical swipes
   * @param {object} e - swipe event parameter.
   */
  swipeVertically(e) {
    this.swipeStart(e);
    this.setState({
      height: this.computeNewHeight(e.deltaY),
      userSetDimensions: true
    });
  }

  /**
   * handle diagonal swipes
   * @param {object} e - swipe event parameter.
   */
  swipeDiagonally(e) {
    this.swipeStart(e);
    this.setState({
      height: this.computeNewHeight(e.deltaY),
      userSetDimensions: true,
      width: this.computeNewWidth(e.deltaX)
    });
  }

  /**
   * disables mouse events on iframe on swipe is start
   * @param {object} e - swipe event parameter
   */
  swipeStart(e) {
    e.preventDefault();
    if (this.props.device !== devices.RESPONSIVE) {
      this.props.deviceSelected(devices.RESPONSIVE);
    }

    (this.ifr.current?.style ?? {}).pointerEvents = 'none';
    (this.ifrMWP?.current?.style ?? {}).pointerEvents = 'none';
  }

  /**
   * enables mouse events on iframe on swipe is end
   */
  swipeEnd() {
    const { handleIsMobileSelection } = this.props;
    const { width } = this.state;
    handleIsMobileSelection(width < devices.RESPONSIVE_940_SIZE.width);

    (this.ifr.current?.style ?? {}).pointerEvents = this.disableContentLinks;
    (this.ifrMWP?.current?.style ?? {}).pointerEvents = 'auto';
  }

  /**
   * toggle whether the canvas iframe is clickable
   */
  toggleContentLinks() {
    const { contentLinkDisabled, previewMetadata } = this.props;
    this.props.setContentLinkStatus(!contentLinkDisabled);

    if (previewMetadata.isPreviewingOnMWP) {
      // send toggle pointer events message to MWP iframe
      this.ifrMWP?.current?.contentWindow.postMessage(
        {
          msg: {
            payload: !contentLinkDisabled ? 'none' : 'auto',
            type: IFRAME_MESSAGE.TOGGLE_POINTER_EVENTS
          },
          type: IFRAME_MESSAGE.CANVAS
        },
        '*'
      );
    }
  }

  /**
   * send current preview content payload to iframe
   */
  updatePreview() {
    const { previewContent, previewMetadata, handleIsMobileSelection } = this.props;
    handleIsMobileSelection(this.state.width < 940);

    // flux action
    const action = {
      payload: {
        previewContent,
        previewMetadata
      },
      type: 'generic'
    };

    if (previewMetadata.isPreviewingOnMWP) {
      action.type = IFRAME_MESSAGE.SEND_PAYLOAD;
      this.ifrMWP?.current?.contentWindow.postMessage(
        { msg: action, type: IFRAME_MESSAGE.CANVAS },
        '*'
      );
      document.addEventListener('click', this.handleOutsideClick);

      action.payload = this.props.contentLinkDisabled ? 'none' : 'auto';
      action.type = IFRAME_MESSAGE.TOGGLE_POINTER_EVENTS;
      this.ifrMWP?.current?.contentWindow.postMessage(
        { msg: action, type: IFRAME_MESSAGE.CANVAS },
        '*'
      );
    } else {
      document.removeEventListener('click', this.handleOutsideClick);
      this.ifr.current?.contentWindow.postMessage(
        { msg: action, type: IFRAME_MESSAGE.CANVAS },
        '*'
      );
    }
  }

  render() {
    const {
      allExperimentVariants,
      contentContextStates,
      contentLinkDisabled,
      device,
      previewMetadata: { frameEnabled, isPreviewingOnMWP, previewSrc },
      hiddenExperiments,
      updateContentContextStates,
      updateHiddenExperiments
    } = this.props;
    const { height, showFullscreenFrame, width } = this.state;

    // used by both frameContainer and frameContainerAndResizers
    const frameDimensions = {
      height: showFullscreenFrame ? '100%' : `${height}px`,
      width: showFullscreenFrame ? '100%' : `${width}px`
    };

    return (
      <Fragment>
        <CanvasControls
          fullscreenStyles={showFullscreenFrame ? styles.fullscreenControls : undefined}
        >
          <ExperimentSelection
            allExperimentVariants={allExperimentVariants}
            hiddenExperiments={hiddenExperiments}
            updateHiddenExperiments={updateHiddenExperiments}
          />
          <RenderPreviewSelection
            selectionStates={contentContextStates}
            updateSelections={updateContentContextStates}
          />
          <CanvasResizer
            device={device}
            width={width}
            height={height}
            onDeviceChange={this.onDeviceChange}
            disableContentLinks={contentLinkDisabled}
            toggleContentLinks={this.toggleContentLinks}
            onWidthChange={this.onWidthChange}
            onHeightChange={this.onHeightChange}
            showFullscreenIframe={showFullscreenFrame}
          />
          <ToggleFullscreen
            onEnterClick={this.onFullscreenEnterToggleClick}
            onExitClick={this.onFullscreenExitToggleClick}
          />
        </CanvasControls>
        <main id="canvasPlane" className={styles.canvasPlane}>
          <div
            id="frameRuler"
            className={
              showFullscreenFrame
                ? styles.fullscreenPositionOverride
                : 'd-flex justify-content-center h-100 w-100'
            }
            ref={this.frameRuler}
          >
            <div
              id="frameContainerAndResizers"
              className="position-relative"
              style={frameDimensions}
            >
              <div
                id={ID.FRAME_CONTAINER}
                className="border border-secondary bg-white shadow"
                style={frameDimensions}
              >
                {frameEnabled && (
                  <React.Fragment>
                    <iframe
                      className="border-0 h-100 w-100"
                      name="previewFrame"
                      onLoad={this.updatePreview}
                      ref={isPreviewingOnMWP ? this.ifrMWP : this.ifr}
                      style={
                        isPreviewingOnMWP ? {} : { pointerEvents: this.disableContentLinks }
                      }
                      src={isPreviewingOnMWP ? previewSrc : PATHNAME_CANVAS}
                      scrolling="auto"
                      title="canvas"
                    />
                  </React.Fragment>
                )}
              </div>
              {!showFullscreenFrame && (
                <Fragment>
                  <DragResizer
                    className={styles.toggleBottom}
                    onSwipe={this.swipeVertically}
                    onSwipeEnd={this.swipeEnd}
                  />
                  <DragResizer
                    className={styles.toggleCorner}
                    onSwipe={this.swipeDiagonally}
                    onSwipeEnd={this.swipeEnd}
                  />
                  <DragResizer
                    className={styles.toggleRight}
                    onSwipe={this.swipeHorizontally}
                    onSwipeEnd={this.swipeEnd}
                  />
                </Fragment>
              )}
            </div>
          </div>
        </main>
      </Fragment>
    );
  }
}
